import { Component, model, input, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Textarea } from 'primeng/textarea';
import { LineItemModel } from '../../../../models/template.model';
import { SpeechService } from '../../../../services/speech.service';
import { ValidationService } from '../../../../services/validation.service';
import { parseValidationConfig, TextValidationConfig } from '../../../../models/validation-config.model';

@Component({
  selector: 'app-textarea-control',
  standalone: true,
  imports: [CommonModule, FormsModule, Textarea],
  template: `
    <div class="checklist-control-row checklist-control-row--top">
      <label [for]="'textarea-' + lineItem().lineItemId" class="checklist-control-label">
        {{ lineItem().name }}@if (lineItem().isRequired) { <span class="required-indicator">*</span>}
      </label>
      <div class="checklist-control-input">
        <textarea
          [id]="'textarea-' + lineItem().lineItemId"
          pInputTextarea
          [(ngModel)]="value"
          [disabled]="disabled()"
          [required]="lineItem().isRequired"
          [maxlength]="maxLength()"
          rows="4"
          class="w-full"
          [ngClass]="{'ng-invalid ng-dirty': hasError()}"
          placeholder="Enter text..."
          (blur)="onBlur()"
        ></textarea>
        @if (hasError()) {
          @for (err of errors(); track $index) {
            <small class="p-error d-block">{{ err }}</small>
          }
        } @else if (hint()) {
          <small class="text-color-secondary">{{ hint() }}</small>
        }
      </div>
      <div class="ctrl-speaker-col">
        <button type="button" class="ctrl-speaker-btn" (click)="speakLabel()"
          [attr.aria-label]="'Read label: ' + lineItem().name">
          <i class="ri-volume-up-line"></i>
        </button>
      </div>
    </div>
  `,
  styles: []
})
export class TextareaControlComponent {
  lineItem = input.required<LineItemModel>();
  value = model<string>('');
  disabled = input<boolean>(false);
  showError = input<boolean>(false);

  private speechService = inject(SpeechService);
  private validationService = inject(ValidationService);

  touched = signal(false);

  errors = computed(() => {
    if (!this.showError() && !this.touched()) return [];
    return this.validationService.validate(this.lineItem(), this.value());
  });

  hasError = computed(() => this.errors().length > 0);

  hint = computed(() => this.hasError() ? '' : this.validationService.getHint(this.lineItem()));

  maxLength = computed(() => {
    const cfg = parseValidationConfig<TextValidationConfig>(this.lineItem().validationConfig);
    return cfg?.maxLength ?? 2000;
  });

  onBlur(): void {
    this.touched.set(true);
  }

  speakLabel(): void {
    this.speechService.speak(this.lineItem().name);
  }
}
